import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useLoading } from "../../../../context/LoadingContext";
import DataTable from "../../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../../Requirement/components/common/SearchFilter";
import AlertModal from "../../../Requirement/components/common/AlerModel";
import { fetchData, searchfetchQueries } from "../../../Requirement/services/RequirementServices";
import config from "../../../../aws-exports";
import ClientForm from "./AddClient";
import { postQueryBackendopsFun } from "../../../Requirement/services/RequirementServices";

const MyClients = () => {
  const [clients, setClients] = useState([]);
  const [activeUser, setActiveUser] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientChanges, setClientChanges] = useState({});
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [activeTab, setActiveTab] = useState("clientDetails");
  const [names, setNames] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [zipcodes, setZipcodes] = useState([]);
  const [countrymaster, setCountryMaster] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const [searchValues, setSearchValues] = useState({
    name: "",
    company: "",
    pin: "",
  });
  const [isAddMode, setIsAddMode] = useState(false);
  const [formErrors, setFormErrors] = useState({
    FirstName: "",
    LastName: "",
    EmailID: "",
    Phone1: "",
    Address1: "",
    Country: "",
  });

  const navigate = useNavigate();
  const { setLoading } = useLoading();

  const getCurrentTimestamp = () => {
    return new Date().toLocaleString('en-US', { 
      timeZone: 'Asia/Kolkata',
      hour12: true,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const validateEmail = useCallback(
    async (email) => {
      if (!email) {
        setFormErrors((prev) => ({ ...prev, EmailID: "Email is required" }));
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setFormErrors((prev) => ({ ...prev, EmailID: "Please enter a valid email address" }));
        return false;
      }

      if (isAddMode) {
        try {
          setLoading(true);
          const response = await fetchData({
            target: "listAMTPLUsers",
            userOrder: "AMTPLUserID",
            rowStart: 0,
            pageSize: 1,
            filterTerms: { EmailID: email },
          });

          if (response.items && response.items.length > 0) {
            setFormErrors((prev) => ({ ...prev, EmailID: "This email is already in use" }));
            return false;
          }
        } catch (error) {
          console.error(`Error checking email at ${getCurrentTimestamp()}: `, error);
          setFormErrors((prev) => ({ ...prev, EmailID: "Error checking email availability" }));
          return false;
        } finally {
          setLoading(false);
        }
      }

      setFormErrors((prev) => ({ ...prev, EmailID: "" }));
      return true;
    },
    [isAddMode, setLoading]
  );

  const validatePhone = useCallback(
    (phone) => {
      const phoneRegex = /^\+?[\d\s-]{10,15}$/;
      if (!phone) {
        setFormErrors((prev) => ({ ...prev, Phone1: "Phone Number is required" }));
        return false;
      } else if (!phoneRegex.test(phone)) {
        setFormErrors((prev) => ({ ...prev, Phone1: "Please enter a valid phone number (10-15 digits)" }));
        return false;
      }
      setFormErrors((prev) => ({ ...prev, Phone1: "" }));
      return true;
    },
    []
  );

  const validateForm = useCallback(
    async (clientData) => {
      const errors = {
        FirstName: "",
        LastName: "",
        EmailID: "",
        Phone1: "",
        Address1: "",
        Country: "",
      };
      let isValid = true;

      if (!clientData.FirstName) {
        errors.FirstName = "First Name is required";
        isValid = false;
      }

      if (!clientData.LastName) {
        errors.LastName = "Last Name is required";
        isValid = false;
      }

      if (!clientData.Country) {
        errors.Country = "Country is required";
        isValid = false;
      }

      if (!await validateEmail(clientData.EmailID || "")) {
        errors.EmailID = formErrors.EmailID || "Email is required";
        isValid = false;
      }

      if (!validatePhone(clientData.Phone1 || "")) {
        errors.Phone1 = formErrors.Phone1 || "Phone Number is required";
        isValid = false;
      }

      if (!clientData.Address1) {
        errors.Address1 = "Address 1 is required";
        isValid = false;
      }

      setFormErrors(errors);
      setIsSaveEnabled(isValid);
      return isValid;
    },
    [validateEmail, validatePhone, formErrors.EmailID, formErrors.Phone1]
  );

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Country" },
        }),
      ]);
      if (allCountries.items.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error(`Error loading countries at ${getCurrentTimestamp()}: `, error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
  };

  const getBackendName = () => {
    const backend = config.aws_user_files_s3_bucket.split("-");
    return backend[backend.length - 1];
  };

  const fetchSearchParams = useCallback(
    async () => {
      setLoading(true);
      try {
        const [initialResponse] = await Promise.all([
          fetchData({
            target: "listAMTPLUsers",
            userOrder: "AMTPLUserID",
            rowStart: 0,
            pageSize: 1,
            filterTerms: { GPEmailID: activeUser },
          }),
        ]);

        const totalCount = initialResponse?.total || initialResponse?.length || 0;

        if (totalCount > 0) {
          const [allItems] = await Promise.all([
            searchfetchQueries({
              target: "listAMTPLUsers",
              userOrder: "AMTPLUserID",
              rowStart: 0,
              pageSize: totalCount,
              filterTerms: { GPEmailID: activeUser },
            }),
          ]);

          if (!Array.isArray(allItems)) {
            console.error(`allItems is not an array at ${getCurrentTimestamp()}:`, allItems);
            setNames([]);
            setCompanies([]);
            setZipcodes([]);
            return;
          }

          const names = allItems
            .filter((item) => item.FirstName || item.LastName)
            .map((item) => ({
              result: `${item.FirstName || ""} ${item.LastName || ""}`.trim(),
            }));
          setNames(names);

          const companies = allItems
            .filter((item) => item.CompanyName)
            .map((item) => ({
              result: item.CompanyName,
            }));
          setCompanies(companies);

          const zipcodes = allItems
            .filter((item) => item.ZipCode || item.CompanyZipCode)
            .map((item) => ({
              result: item.ZipCode || item.CompanyZipCode,
            }));
          setZipcodes(zipcodes);
        } else {
          setNames([]);
          setCompanies([]);
          setZipcodes([]);
        }
      } catch (error) {
        console.error(`Error loading search parameters at ${getCurrentTimestamp()}: `, error);
        setNames([]);
        setCompanies([]);
        setZipcodes([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, activeUser]
  );

  const searchMyClients = useCallback(
    async (searchValues, newRowStart = rowStart) => {
      setLoading(true);
      try {
        const body = {
          target: "listAMTPLUsers",
          userOrder: "AMTPLUserID",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: { ...searchValues, GPEmailID: activeUser },
        };
        const response = await fetchData(body);
        setClients(response.items || []);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error(`Error loading clients at ${getCurrentTimestamp()}: `, error);
        setClients([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, activeUser, setLoading]
  );

  const handleSearch = (searchValues) => {
    setRowStart(0);
    searchMyClients(searchValues, 0);
  };

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    searchMyClients(searchValues, newRowStart);
  };

  const handleRowClick = (client) => {
    setSelectedClient(client);
    setClientChanges(client);
    setIsSaveEnabled(false);
    setActiveTab("clientDetails");
    setIsAddMode(false);
    setFormErrors({ FirstName: "", LastName: "", EmailID: "", Phone1: "", Address1: "", Country: "" });
  };

  const handleAddClick = () => {
    setSelectedClient(null);
    setClientChanges({});
    setIsSaveEnabled(false);
    setActiveTab("clientDetails");
    setIsAddMode(true);
    setFormErrors({ FirstName: "", LastName: "", EmailID: "", Phone1: "", Address1: "", Country: "" });
  };

  const handleCancelAdd = () => {
    setIsAddMode(false);
    setSelectedClient(null);
    setClientChanges({});
    setIsSaveEnabled(false);
    setFormErrors({ FirstName: "", LastName: "", EmailID: "", Phone1: "", Address1: "", Country: "" });
  };

  const handleSave = async () => {
    const isValid = await validateForm(clientChanges);
    const timestamp = getCurrentTimestamp();
    
    if (!isValid) {
      setModal({
        show: true,
        type: "error",
        title: "Validation Error",
        message: `Please fill out all required fields correctly. Error at ${timestamp}`,
        autoClose: false,
      });
      return;
    }

    setLoading(true);
    try {
      const body = {
        target: "addusersmanual",
        userData: {
          ...((clientChanges) => {
            const { errors, ...rest } = clientChanges;
            return rest;
          })(clientChanges),
          GPEmailID: activeUser,
          password: "zXCVBNM123456789!",
          UserType: "buyer",
        },
      };
      console.log(`API call initiated at ${timestamp}:`, body);
      const result = await postQueryBackendopsFun(body);
      
      if (result.success) {
        setClients((prev) =>
          isAddMode
            ? [...prev, clientChanges]
            : prev.map((client) =>
              client.AMTPLUserID === clientChanges.AMTPLUserID ? clientChanges : client
            )
        );
        setSelectedClient(null);
        setClientChanges({});
        setIsAddMode(false);
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: `Client ${isAddMode ? "added" : "updated"} successfully at ${timestamp}`,
          autoClose: false,
        });
      } else {
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: `${result.error} at ${timestamp}`,
          autoClose: false,
        });
      }
    } catch (error) {
      console.error(`Error invoking Lambda function at ${timestamp}:`, error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `An error occurred while saving the client at ${timestamp}`,
        autoClose: false,
      });
    } finally {
      setLoading(false);
      setIsSaveEnabled(false);
    }
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;
  const fields = [
    {
      name: "name",
      label: "Name",
      type: "dropdown",
      options: (names || [])
        .filter((item) => item.result && item.result.trim() !== "")
        .map((item) => ({
          value: item.result,
          label: item.result,
        })),
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, name: value }));
      },
      value: searchValues.name,
    },
    {
      name: "company",
      label: "Company",
      type: "dropdown",
      options: (companies || [])
        .filter((item) => item.result && item.result.trim() !== "")
        .map((item) => ({
          value: item.result,
          label: item.result,
        })),
      value: searchValues.company,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, company: value }));
      },
    },
    {
      name: "pin",
      label: "PIN/ZIP Code",
      type: "dropdown",
      options: (zipcodes || [])
        .filter((item) => item.result && item.result.trim() !== "")
        .map((item) => ({
          value: item.result,
          label: item.result,
        })),
      value: searchValues.pin,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, pin: value }));
      },
    },
  ];

  const columns = [
    { id: "EmailID", numeric: false, label: "Buyer Email" },
    {
      id: "Name",
      numeric: false,
      label: "Buyer Name",
      renderCell: (row) => `${row.FirstName} ${row.LastName || ''}`,
    },
    { id: "CompanyName", numeric: false, label: "Company" },
    { id: "Country", numeric: false, label: "Country" },
    { id: "Phone1", numeric: false, label: "Phone" },
    {
      id: "Status",
      numeric: false,
      label: "Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.Status) {
          case "INACTIVE":
            bgColor = "#f0a6898e";
            textColor = "#b41209ff";
            break;
          case "ACTIVE":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#f3f4f6";
            textColor = "#374151";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.65rem !important",
              fontWeight: 500,
            }}
          >
            {row.Status}
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        if (!decodedToken["cognito:groups"]?.includes("AMTPL_GLOBAL_PARTNERS")) {
          await Auth.signOut();
          navigate("/login/global-partner");
        } else {
          setActiveUser(user.attributes.email);
        }
      } catch (error) {
        await Auth.signOut();
        navigate("/login/global-partner");
      }
    };
    checkUser();
    loadCountries();
  }, [navigate, loadCountries]);

  useEffect(() => {
    if (activeUser) {
      fetchSearchParams();
    }
  }, [activeUser, fetchSearchParams]);

  useEffect(() => {
    if (activeUser) {
      searchMyClients(searchValues);
    }
  }, [rowStart, pageSize, activeUser, searchValues, searchMyClients]);

  return (
    <div className="buyer-layout">
      <div className="amtpl-tabs">
        <div className="content3">
          <main className="main-content">
            <AlertModal
              type={modal.type}
              title={modal.title}
              message={modal.message}
              showModal={modal.show}
              onClose={handleModalClose}
              buttonText="OK"
              autoClose={modal.autoClose}
            />
            <div className="search-filter4">
              <div className="title4">
                <div className="head-text">My Buyers</div>
              </div>
              <div className="filter4">
                <SearchFilter fields={fields} onSearch={handleSearch} />
              </div>
              <button className="submit-btn" onClick={handleAddClick}>
                Add
              </button>
            </div>
            <DataTable
              columns={columns}
              data={clients}
              total={totalItems}
              title="Client Information"
              onRowClick={handleRowClick}
              rowsPerPage={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
            {(selectedClient || isAddMode) && (
              <ClientForm
                selectedClient={selectedClient}
                clientChanges={clientChanges}
                setClientChanges={setClientChanges}
                handleSave={handleSave}
                isSaveEnabled={isSaveEnabled}
                setIsSaveEnabled={setIsSaveEnabled}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isAddMode={isAddMode}
                onCancel={handleCancelAdd}
                validateEmail={validateEmail}
                validatePhone={validatePhone}
                formErrors={formErrors}
                countrymaster={countrymaster}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default MyClients;